import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from "@mui/material";
import {
  Search as SearchIcon,
  GetApp as DownloadIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import apiService, { StripePurchaseHistoryRow } from "../services/api";
import { useThemeMode } from "../contexts/ThemeContext";

type FilterState = {
  startDate: Date | null;
  endDate: Date | null;
  type: "all" | "extra_pages" | "plan";
  search: string;
};

const PurchaseHistory: React.FC = () => {
  //const theme = useTheme();
  const { mode } = useThemeMode();
  const [history, setHistory] = useState<StripePurchaseHistoryRow[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<StripePurchaseHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    startDate: null,
    endDate: null,
    type: "all",
    search: ""
  });

  // Summary stats
  const [stats, setStats] = useState({
    totalSpent: 0,
    totalPurchases: 0,
    averagePerPurchase: 0,
    mostRecent: null as Date | null
  });

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getPurchaseHistory();
      setHistory(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load purchase history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Apply filters and calculate stats
  useEffect(() => {
    let filtered = [...history];

    // Apply type filter
    if (filters.type !== "all") {
      filtered = filtered.filter(item => 
        filters.type === "extra_pages" ? item.pagesPurchased > 0 : item.pagesPurchased === 0
      );
    }

    // Apply date range filter
    if (filters.startDate) {
      filtered = filtered.filter(item => 
        item.createdOn && new Date(item.createdOn) >= filters.startDate!
      );
    }
    if (filters.endDate) {
      filtered = filtered.filter(item => 
        item.createdOn && new Date(item.createdOn) <= filters.endDate!
      );
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item =>
        item.stripeSessionId?.toLowerCase().includes(searchLower) ||
        item.eventId?.toLowerCase().includes(searchLower) ||
        item.planName?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredHistory(filtered);

const PLAN_PRICE_BY_PAGES: Record<number, number> = {
  100: 29,
  200: 39,
  400: 79,
  800: 149,
  1500: 229,
  2200: 299,
  3200: 399,
  4500: 599,
  7000: 899,
  1200: 261,
  2400: 361,
  4800: 711,
  9600: 1341,
  18000: 2061,
  26400: 2691,
  38400: 3591,
  54000: 5391,
  84000: 8091
};

const EXTRA_PRICE_BY_PAGES: Record<number, number> = {
  100: 35,
  500: 125,
  1000: 200
};

const getPurchaseAmount = (item: any): number => {
  const pages = Number(item.pagesPurchased ?? 0);
  const name = String(item.planName ?? item.displayType ?? "").toLowerCase();

  const isExtraPages = name.includes("extra pages");

  if (isExtraPages) {
    return EXTRA_PRICE_BY_PAGES[pages] ?? 0;
  }

  return PLAN_PRICE_BY_PAGES[pages] ?? 0;
};





    // Calculate stats
    const total = filtered.reduce(
  (sum, item) => sum + getPurchaseAmount(item),
  0
);
    const dates = filtered.map(f => f.createdOn ? new Date(f.createdOn) : null).filter(Boolean) as Date[];
    
    setStats({
      totalSpent: total,
      totalPurchases: filtered.length,
      averagePerPurchase: filtered.length ? total / filtered.length : 0,
      mostRecent: dates.length ? new Date(Math.max(...dates.map(d => d.getTime()))) : null
    });
  }, [history, filters]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    const csvRows = [
      ['Date', 'Type', 'Plan Name', 'Session ID', 'Event ID', 'Status', 'Pages'],
      ...filteredHistory.map(row => [
        row.createdOn || '',
        row.pagesPurchased > 0 ? 'Extra Pages' : 'Plan',
        row.planName || (row.pagesPurchased > 0 ? '' : 'Plan'),
        row.stripeSessionId || '',
        row.eventId || '',
        row.stateCode === 0 ? 'Inactive' : 'Active',
        row.pagesPurchased > 0 ? row.pagesPurchased.toString() : ''
      ])
    ];
    
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      type: "all",
      search: ""
    });
  };

 // Helper to get display name for purchase type
const getPurchaseDisplayName = (row: StripePurchaseHistoryRow) => {
  // If there's a plan name, show it
  if (row.planName) {
    return row.planName;
  }
  // If it's extra pages
  if (row.pagesPurchased > 0) {
    return `Extra Pages (${row.pagesPurchased})`;
  }
  return "Plan";
};

  if (loading) {
    return (
      <Container sx={{ mt: 6, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            background: "linear-gradient(135deg, #0A1E3C 0%, #1E3A5F 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1
          }}
        >
          Purchase History
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track all your transactions and subscription payments
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #0A1E3C 0%, #1E3A5F 100%)",
              color: "white",
              borderRadius: 3,
              boxShadow: "0 8px 24px rgba(10, 30, 60, 0.15)"
            }}
          >
            <CardContent>
              <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.7)" }}>
                Total Purchases
              </Typography>
              <Typography variant="h4" fontWeight={800}>
                {stats.totalPurchases}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid rgba(253, 187, 48, 0.2)" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total Spent
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#FDBB30">
                ${stats.totalSpent.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid rgba(42, 157, 143, 0.2)" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Average per Purchase
              </Typography>
              <Typography variant="h4" fontWeight={800} color="#2A9D8F">
                ${stats.averagePerPurchase.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid rgba(155, 135, 245, 0.2)" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Most Recent
              </Typography>
              <Typography variant="h6" fontWeight={600} color="#9B87F5">
                {stats.mostRecent?.toLocaleDateString() || "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${mode === 'light' ? 'rgba(10, 30, 60, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.02)"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <TextField
            placeholder="Search by Session, Event ID, or Plan Name"
            size="small"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#0A1E3C" }} />
                </InputAdornment>
              )
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Purchase Type</InputLabel>
            <Select
              value={filters.type}
              label="Purchase Type"
              onChange={(e) => setFilters({ ...filters, type: e.target.value as any })}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="plan">Plans</MenuItem>
              <MenuItem value="extra_pages">Extra Pages</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              borderColor: "#0A1E3C",
              color: "#0A1E3C",
              "&:hover": {
                borderColor: "#FDBB30",
                bgcolor: "rgba(253, 187, 48, 0.04)"
              }
            }}
          >
            Date Filter
          </Button>

          {Object.values(filters).some(v => v !== null && v !== "" && v !== "all") && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ color: "#d32f2f" }}
            >
              Clear
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{
              ml: "auto",
              background: "linear-gradient(135deg, #FDBB30 0%, #FFD966 100%)",
              color: "#0A1E3C",
              "&:hover": {
                background: "linear-gradient(135deg, #FFD966 0%, #FDBB30 100%)"
              }
            }}
          >
            Export CSV
          </Button>
        </Box>

        {showFilters && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(date) => setFilters({ ...filters, startDate: date })}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(date) => setFilters({ ...filters, endDate: date })}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>
        )}
      </Paper>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : filteredHistory.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
          <ReceiptIcon sx={{ fontSize: 60, color: "#FDBB30", opacity: 0.5, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No purchase history found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your transactions will appear here once you make a purchase
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${mode === 'light' ? 'rgba(10, 30, 60, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.04)"
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: mode === 'light' ? 'rgba(10, 30, 60, 0.02)' : 'rgba(255, 255, 255, 0.02)' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Type / Plan</TableCell>
                  <TableCell>Session ID</TableCell>
                  <TableCell>Event ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Pages</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={`${row.eventId}-${row.stripeSessionId}`}
                      sx={{
                        "&:hover": {
                          bgcolor: mode === 'light' 
                            ? 'rgba(253, 187, 48, 0.02)' 
                            : 'rgba(253, 187, 48, 0.04)'
                        }
                      }}
                    >
                      <TableCell>
                        {row.createdOn ? new Date(row.createdOn).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPurchaseDisplayName(row)}
                          size="small"
                          sx={{
                            bgcolor: row.pagesPurchased > 0 
                              ? "rgba(42, 157, 143, 0.1)" 
                              : "rgba(253, 187, 48, 0.1)",
                            color: row.pagesPurchased > 0 ? "#2A9D8F" : "#FDBB30",
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {row.stripeSessionId?.slice(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                          {row.eventId?.slice(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                        label={row.stateCode === 0 ? "Active" : "Inactive"}  // 0=Active, 1=Inactive
                        size="small"
                        color={row.stateCode === 0 ? "success" : "default"}
                        variant="outlined"
                      />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          {row.pagesPurchased > 0 ? row.pagesPurchased : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredHistory.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Container>
  );
};

export default PurchaseHistory;